import React, { useEffect, useState } from "react";
import { Card, Row, Col, Input, InputNumber, Button, message, Spin } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { savePacking, fetchPackingList } from "./packingThunks";
import { clearMessages, setIsEditPacking, setSelectedPacking } from "./packingSlice";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import { fetchItems } from "../../../store/commonThunks/lookups";
import { useNavigate } from "react-router-dom";

const packLabels = [
  { key: "std", label: "Standard Pack" },
  { key: "second", label: "2nd Pack" },
  { key: "third", label: "3rd Pack" },
  { key: "fourth", label: "4th Pack" },
  { key: "fifth", label: "5th Pack" },
  { key: "sixth", label: "6th Pack" },
];

const PackingStructureForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items } = useSelector((state) => state.lookups);
  const { selectedPacking, isEditPacking, loadingSave, successMessage, error } =
    useSelector((state) => state.packing);

  const [formState, setFormState] = useState({
    item_id: null,
    item_unit: "", // base unit (for validation)
    retailRate: 0, // base retail rate (for auto-calc)
    stdPackName: "",
    stdPackQty: "",
    stdRate: "",
    secondPackName: "",
    secondPackQty: "",
    secondRate: "",
    thirdPackName: "",
    thirdPackQty: "",
    thirdRate: "",
    fourthPackName: "",
    fourthPackQty: "",
    fourthRate: "",
    fifthPackName: "",
    fifthPackQty: "",
    fifthRate: "",
    sixthPackName: "",
    sixthPackQty: "",
    sixthRate: "",
  });

  const [visiblePacks, setVisiblePacks] = useState(1);

 useEffect(() => {
  if (isEditPacking && selectedPacking) {
    dispatch(fetchItems());
    const matchedItem = items?.find((i) => i.item_id === selectedPacking.item_id);

    setFormState({
      ...selectedPacking,
      retailRate: matchedItem?.sale_rate || 0,
      item_unit: matchedItem?.unit_name || "",
    });

    let maxPack = 1;
    packLabels.forEach((p, i) => {
      if (selectedPacking[`${p.key}PackName`]) maxPack = i + 1;
    });
    setVisiblePacks(maxPack);
  }
  // 👇 notice: no selectedPacking, no items here
}, [isEditPacking]);



  useEffect(() => {
    dispatch(fetchItems());
  }, [dispatch]);

  useEffect(() => {
    if (successMessage) {
      message.success(successMessage);
      dispatch(clearMessages());
      navigate("/inventory/product?tab=packing");
    }
    if (error) {
      message.error(error);
      dispatch(clearMessages());
    }
  }, [successMessage, error, dispatch, navigate]);

  // ✅ Validation
  const validateForm = () => {
    if (!formState.item_id) {
      message.warning("Please select an Item");
      return false;
    }
    if (!formState.stdPackName) {
      message.warning("Standard Pack Name is required");
      return false;
    }

    let lastValidIndex = -1;
    for (let i = 0; i < packLabels.length; i++) {
      const p = packLabels[i];
      const name = formState[`${p.key}PackName`];
      const qty = formState[`${p.key}PackQty`];

      // prevent using base unit
      if (name && name === formState.item_unit) {
        message.warning(`${p.label} name cannot be the same as base unit`);
        return false;
      }

      // name length limit
      if (name && name.length > 50) {
        message.warning(`${p.label} name should not exceed 50 characters`);
        return false;
      }

      if (name && (!qty || qty <= 0)) {
        message.warning(`${p.label} requires valid quantity`);
        return false;
      }
      if (qty && (!name || qty <= 0)) {
        message.warning(`${p.label} requires valid name`);
        return false;
      }

      // sequential packs
      if (name) {
        if (i > lastValidIndex + 1) {
          message.warning(
            `Please enter ${packLabels[lastValidIndex + 1].label} before ${p.label}`
          );
          return false;
        }
        lastValidIndex = i;
      }
    }
    return true;
  };

  // ✅ Auto-calc rate when qty changes
  const handleQtyChange = (key, qty) => {
  setFormState((prev) => {
   if (!qty || !prev.retailRate) {
      return {
        ...prev,
        [`${key}PackQty`]: qty,
        // keep old rate instead of blank
        [`${key}Rate`]: prev[`${key}Rate`],
      };
    }

    return {
      ...prev,
      [`${key}PackQty`]: qty,
      [`${key}Rate`]: (qty * prev.retailRate).toFixed(2),
    };
  });
};


  const handleSave = () => {
    if (!validateForm()) return;
    dispatch(savePacking(formState)).then(() => {
      dispatch(fetchPackingList());
      dispatch(setIsEditPacking(false));
      dispatch(setSelectedPacking(null));
    });
  };

  return (
    <Spin spinning={loadingSave}>
    <Card
      title={isEditPacking ? "Edit Packing Structure" : "Add New Packing"}
      style={{ marginTop: 24 }}
    >
      {/* Item Selector */}
      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={24} md={8}>       
          <label>Item <span style={{ color: "#ff4d4f" }}>*</span></label>
          <SearchListAutoComplete
            entityType="item"
            style={{ width: "100%" }}            
            optionsList={items?.map((i) => ({
              sale_id: i.item_id,
              display_text: i.item_name,
              unit_name: i.unit_name,
              retailRate: i.sale_rate,
              raw: i,
            }))}
            value={
                formState.item_id
                  ? { key: formState.item_id, value: formState.itemName.toString(), label: formState.itemName,  }
                  : null
              }

            onSelect={(obj) =>
              setFormState((prev) => ({
                ...prev,
                item_id: obj?.sale_id,
                itemName: obj?.display_text,
                item_unit: obj?.unit_name,
                retailRate: obj?.retailRate || 0,      
                stdRate: prev.stdPackQty ? (prev.stdPackQty * (obj?.retailRate || 0)).toFixed(2) : "",          
              }))
            }
            placeholder="Item"
          />
        </Col>
      </Row>

      {/* Dynamic Packs */} {console.log(packLabels)}
      {packLabels.slice(0, visiblePacks).map((p) => (
        <Row gutter={[16, 16]} key={p.key} style={{ marginBottom: 12 }}>
          <Col xs={24} md={8}>
            <label>{p.label} Name</label>
            <Input
              value={formState[`${p.key}PackName`]}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  [`${p.key}PackName`]: e.target.value,
                })
              }
            />
          </Col>
          <Col xs={24} md={8}>
            <label>{p.label} Quantity</label>
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              onWheel={(e) => e.target.blur()}
              value={formState[`${p.key}PackQty`] || ""}
              onChange={(val) => handleQtyChange(p.key, val)}
            />
          </Col>
          <Col xs={24} md={8}>
            <label>{p.label} Rate</label>
            <InputNumber
              disabled
              style={{ width: "100%" }}
              onWheel={(e) => e.target.blur()}
              value={formState[`${p.key}Rate`] || ""}
            />
          </Col>
        </Row>
      ))}

      {/* Add Pack Button */}
      {visiblePacks < 6 && (
        <Row>
          <Col span={24}>
            <Button onClick={() => setVisiblePacks(visiblePacks + 1)} icon={<PlusOutlined />}>
              Add New
            </Button>

          </Col>
        </Row>
      )}

      {/* Actions */}
      <Row style={{ marginTop: 16 }} gutter={16}>
        <Col>
          <Button type="primary" onClick={handleSave} loading={loadingSave}>
            {isEditPacking ? "Update" : "Save"}
          </Button>
        </Col>
        <Col>
          <Button
            onClick={() => {
              navigate("/inventory/product?tab=packing");
              dispatch(setIsEditPacking(false));
              dispatch(setSelectedPacking(null));
            }}
          >
            Cancel
          </Button>
        </Col>
      </Row>
    </Card>
    </Spin>
  );
};

export default PackingStructureForm;
